/* PDP reviews — behaviour for sections/pdp-reviews.liquid.

   Reads reviews from Judge.me's public widget API and renders them as the
   section's cards, with the section's filter pills, rating summary, "Show
   more", "Read more" and photo viewer.

   Why the API's HTML is parsed rather than rendered
   The public token only reaches Judge.me's widget endpoints, and those answer
   with the markup of Judge.me's own widget, not with JSON. That markup is
   parsed with DOMParser — an inert document, so nothing in it runs or loads —
   and only text and image URLs are read out of it. Every node on the page is
   built here with textContent, so no string from the API is ever inserted as
   HTML.

   Endpoints used (all public, all CORS-enabled, all cached by Judge.me for 20
   minutes):
   - widgets/product_review   one product, up to 100 reviews per request
   - widgets/all_reviews_page shop-wide, 25 per request whatever per_page says,
                              plus a header carrying the shop's average/count

   Scoped entirely to <pdp-reviews>: no globals besides the element itself, no
   listeners outside it except the theme editor's block-select event, which is
   removed again when the section is unloaded. */

(() => {
  if (customElements.get('pdp-reviews')) return;

  const API = 'https://judge.me/api/v1/widgets/';
  const PRODUCT_PAGE_SIZE = 100;
  const SHOP_PAGE_SIZE = 25;
  const MAX_PRODUCT_PAGES = 5;
  const CACHE_TTL = 20 * 60 * 1000; // Judge.me's own max-age=1200
  const CACHE_PREFIX = 'pdp-reviews:v1:';
  const MAX_PHOTOS = 6;
  const STAR_PATH =
    'M10 1.3l2.63 5.33 5.88.86-4.26 4.15 1.01 5.86L10 14.73l-5.26 2.77 1.01-5.86L1.49 7.49l5.88-.86z';

  /* Session cache ------------------------------------------------------------
     Parsed records, not raw HTML, so a return visit within the TTL costs no
     request and no parse. Every access is guarded: storage can be full,
     disabled or throw in a private window, and the section must still work. */

  const cacheGet = (key) => {
    try {
      const raw = sessionStorage.getItem(CACHE_PREFIX + key);
      if (!raw) return null;
      const entry = JSON.parse(raw);
      return Date.now() - entry.t < CACHE_TTL ? entry.v : null;
    } catch (error) {
      return null;
    }
  };

  const cacheSet = (key, value) => {
    try {
      sessionStorage.setItem(CACHE_PREFIX + key, JSON.stringify({ t: Date.now(), v: value }));
    } catch (error) {
      /* Storage full or unavailable: the next visit simply fetches again. */
    }
  };

  const loadParsed = async (url, parse) => {
    const cached = cacheGet(url);
    if (cached) return cached;
    const response = await fetch(url, { credentials: 'omit' });
    if (!response.ok) throw new Error(`Judge.me responded ${response.status}`);
    const parsed = parse(await response.json());
    cacheSet(url, parsed);
    return parsed;
  };

  /* Parsing ------------------------------------------------------------------ */

  const cleanText = (node) => (node ? node.textContent.replace(/\s+/g, ' ').trim() : '');

  const isSafeUrl = (url) => {
    if (!url) return false;
    try {
      return new URL(url, window.location.href).protocol === 'https:';
    } catch (error) {
      return false;
    }
  };

  const handleOf = (url) => {
    if (!url) return '';
    try {
      const path = decodeURIComponent(new URL(url, window.location.origin).pathname);
      const match = path.match(/\/products\/([^/?#]+)/);
      return match ? match[1] : '';
    } catch (error) {
      return '';
    }
  };

  const parseDocument = (html) => new DOMParser().parseFromString(html || '', 'text/html');

  const parseReviews = (doc) =>
    Array.from(doc.querySelectorAll('.jdgm-rev[data-review-id]')).map((node) => {
      const body = node.querySelector('.jdgm-rev__body');
      let paragraphs = body ? Array.from(body.querySelectorAll('p')).map(cleanText).filter(Boolean) : [];
      if (!paragraphs.length && body && cleanText(body)) paragraphs = [cleanText(body)];

      const photos = Array.from(node.querySelectorAll('.jdgm-rev__pic-link'))
        .map((link) => {
          const img = link.querySelector('img');
          return {
            full: link.getAttribute('data-mfp-src') || link.getAttribute('href'),
            thumb: img ? img.getAttribute('data-src') || img.getAttribute('src') : '',
          };
        })
        .filter((photo) => isSafeUrl(photo.full) && isSafeUrl(photo.thumb));

      const productLink = node.querySelector('.jdgm-rev__prod-link');
      const rating = node.querySelector('.jdgm-rev__rating');
      const time = node.querySelector('.jdgm-rev__timestamp');

      return {
        id: node.getAttribute('data-review-id'),
        rating: Number(rating && rating.getAttribute('data-score')) || 0,
        verified: node.getAttribute('data-verified-buyer') === 'true',
        author: cleanText(node.querySelector('.jdgm-rev__author')),
        location: cleanText(node.querySelector('.jdgm-rev__location')),
        title: cleanText(node.querySelector('.jdgm-rev__title')),
        paragraphs,
        date: time ? time.getAttribute('datetime') || '' : '',
        photos,
        productTitle: node.getAttribute('data-product-title') || cleanText(productLink),
        productUrl: node.getAttribute('data-product-url') || (productLink && productLink.getAttribute('href')) || '',
      };
    });

  const parseProductWidget = (data) => {
    const doc = parseDocument(data && data.widget);
    const root = doc.querySelector('.jdgm-rev-widg');
    return {
      reviews: parseReviews(doc),
      average: Number(root && root.getAttribute('data-average-rating')) || 0,
      count: Number(root && root.getAttribute('data-number-of-reviews')) || 0,
    };
  };

  const parseShopPage = (data) => {
    const header = parseDocument(data && data.all_reviews_header).querySelector('.jdgm-all-reviews__header');
    return {
      reviews: parseReviews(parseDocument(data && data.all_reviews)),
      average: Number(header && header.getAttribute('data-average-rating')) || 0,
      count: Number(header && header.getAttribute('data-number-of-reviews')) || 0,
    };
  };

  /* Filters ------------------------------------------------------------------
     Keywords match at the start of a word, in any script: "allerg" finds
     "allergic", while "itch" does not fire inside "switched". */

  const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  const reviewText = (review) => `${review.title} ${review.paragraphs.join(' ')}`;

  const buildPredicate = (button) => {
    const type = button.dataset.filter;
    if (type === 'photos') return (review) => review.photos.length > 0;
    if (type === 'rating') {
      const min = Number(button.dataset.rating) || 5;
      return (review) => review.rating >= min;
    }
    if (type === 'keywords') {
      const words = (button.dataset.keywords || '')
        .split(/[,\n]/)
        .map((word) => word.trim().toLowerCase())
        .filter(Boolean)
        .map(escapeRegExp);
      if (!words.length) return () => false;
      let pattern;
      try {
        pattern = new RegExp(`(^|[^\\p{L}\\p{N}])(${words.join('|')})`, 'iu');
      } catch (error) {
        pattern = new RegExp(`(^|[^a-z0-9])(${words.join('|')})`, 'i');
      }
      return (review) => pattern.test(reviewText(review));
    }
    return () => true;
  };

  /* DOM helpers --------------------------------------------------------------- */

  const create = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text) node.textContent = text;
    return node;
  };

  const starRow = (className) =>
    `<span class="${className}" aria-hidden="true">${`<svg viewBox="0 0 20 20" focusable="false"><path d="${STAR_PATH}"/></svg>`.repeat(5)}</span>`;

  /* Two identical rows of stars, the coloured one clipped to the rating, so a
     4.8 average reads as four and four-fifths stars rather than rounding. */
  const fillStars = (node, rating, label) => {
    const percent = Math.max(0, Math.min(100, (rating / 5) * 100));
    node.setAttribute('role', 'img');
    node.setAttribute('aria-label', label);
    node.style.setProperty('--pdp-reviews-fill', `${percent}%`);
    node.innerHTML = starRow('pdp-reviews__stars-base') + starRow('pdp-reviews__stars-fill');
    return node;
  };

  class PdpReviews extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;

      const data = this.dataset;
      this.settings = {
        shop: data.shop,
        token: data.token,
        source: data.source || 'product_then_shop',
        productId: data.productId || '',
        handle: data.productHandle || '',
        shopLimit: Number(data.shopLimit) || 100,
        perPage: Number(data.perPage) || 6,
        minRating: Number(data.minRating) || 1,
        minLength: Number(data.minLength) || 0,
        clamp: Number(data.clamp) || 0,
        sort: data.sort || 'judgeme',
        summarySource: data.summarySource || 'shop',
        caption: data.caption || '',
        showDate: data.showDate === 'true',
        showProduct: data.showProduct === 'true',
        hideEmptyTabs: data.hideEmptyTabs === 'true',
        designMode: data.designMode === 'true',
      };
      this.labels = {
        verified: data.labelVerified || 'Verified buyer',
        readMore: data.labelReadMore || 'Read more',
        readLess: data.labelReadLess || 'Read less',
        product: data.labelProduct || 'Reviewing',
      };

      this.grid = this.querySelector('[data-grid]');
      this.emptyMessage = this.querySelector('[data-empty]');
      this.moreButton = this.querySelector('[data-more]');
      this.status = this.querySelector('[data-status]');
      this.summary = this.querySelector('[data-summary]');
      this.tabGroup = this.querySelector('[data-tabs]');
      this.tabs = Array.from(this.querySelectorAll('.pdp-reviews__tab'));
      this.lightbox = this.querySelector('[data-lightbox]');
      this.lightboxImage = this.querySelector('[data-lightbox-img]');
      this.photoLabel = (this.lightbox && this.lightbox.getAttribute('aria-label')) || 'Review photo';

      this.tabs.forEach((tab) => {
        tab.predicate = buildPredicate(tab);
        tab.disabled = true;
      });

      this.onTabClick = this.onTabClick.bind(this);
      this.onMoreClick = this.onMoreClick.bind(this);
      this.onGridClick = this.onGridClick.bind(this);
      this.onBlockSelect = this.onBlockSelect.bind(this);
      this.onLightboxClick = this.onLightboxClick.bind(this);

      if (this.tabGroup) this.tabGroup.addEventListener('click', this.onTabClick);
      this.moreButton.addEventListener('click', this.onMoreClick);
      this.grid.addEventListener('click', this.onGridClick);
      if (this.lightbox) this.lightbox.addEventListener('click', this.onLightboxClick);
      if (this.settings.designMode) document.addEventListener('shopify:block:select', this.onBlockSelect);

      this.whenNearViewport(() => this.load());
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
    }

    /* The request waits until the section is close to being seen, so a
       shopper who never scrolls this far never pays for it. The editor loads
       straight away so the merchant is not left looking at placeholders. */
    whenNearViewport(callback) {
      if (this.settings.designMode || !('IntersectionObserver' in window)) {
        callback();
        return;
      }
      this.observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          this.observer.disconnect();
          callback();
        },
        { rootMargin: '800px 0px' }
      );
      this.observer.observe(this);
    }

    get query() {
      return `shop_domain=${encodeURIComponent(this.settings.shop)}&api_token=${encodeURIComponent(this.settings.token)}`;
    }

    async fetchProduct() {
      const base = `${API}product_review?${this.query}&external_id=${encodeURIComponent(
        this.settings.productId
      )}&per_page=${PRODUCT_PAGE_SIZE}`;
      const first = await loadParsed(`${base}&page=1`, parseProductWidget);
      const pages = Math.min(Math.ceil(first.count / PRODUCT_PAGE_SIZE), MAX_PRODUCT_PAGES);
      const rest = await Promise.all(
        Array.from({ length: Math.max(0, pages - 1) }, (_, index) =>
          loadParsed(`${base}&page=${index + 2}`, parseProductWidget).catch(() => null)
        )
      );
      rest.forEach((page) => page && first.reviews.push(...page.reviews));
      return first;
    }

    fetchShopPage(page) {
      return loadParsed(`${API}all_reviews_page?${this.query}&page=${page}`, parseShopPage);
    }

    async load() {
      const { source, summarySource, productId, shop, token } = this.settings;
      if (!shop || !token) {
        this.fail('Add the Judge.me public API token in this section’s settings.');
        return;
      }

      const wantsProduct = source !== 'shop' && productId;
      const wantsShopReviews = source !== 'product';
      const wantsShopHeader = wantsShopReviews || summarySource === 'shop';

      try {
        const [product, shopFirst] = await Promise.all([
          wantsProduct ? this.fetchProduct().catch(() => null) : null,
          wantsShopHeader ? this.fetchShopPage(1).catch(() => null) : null,
        ]);

        let shopReviews = [];
        if (wantsShopReviews && shopFirst) {
          shopReviews = shopFirst.reviews.slice();
          const pages = Math.min(
            Math.ceil(this.settings.shopLimit / SHOP_PAGE_SIZE),
            Math.ceil(shopFirst.count / SHOP_PAGE_SIZE)
          );
          const rest = await Promise.all(
            Array.from({ length: Math.max(0, pages - 1) }, (_, index) =>
              this.fetchShopPage(index + 2).catch(() => null)
            )
          );
          rest.forEach((page) => page && shopReviews.push(...page.reviews));
        }

        this.renderSummary(summarySource === 'product' ? product : shopFirst || product);
        this.setReviews(product ? product.reviews : [], shopReviews);
      } catch (error) {
        console.warn('[pdp-reviews]', error);
        this.fail('Judge.me reviews could not be loaded. Check the public API token.');
      }
    }

    renderSummary(stats) {
      if (!this.summary) return;
      if (!stats || !stats.count) {
        this.summary.hidden = true;
        return;
      }
      const average = stats.average.toFixed(1);
      this.summary.querySelector('[data-summary-average]').textContent = average;
      fillStars(
        this.summary.querySelector('[data-summary-stars]'),
        stats.average,
        `Average rating ${average} out of 5`
      );
      this.summary.querySelector('[data-summary-caption]').textContent = this.settings.caption.replace(
        /\[count\]/g,
        stats.count.toLocaleString(document.documentElement.lang || undefined)
      );
      this.summary.classList.remove('is-loading');
    }

    /* Product reviews always lead, then shop-wide ones; each group is sorted
       on its own so "product first" survives any sort order. A review the
       product endpoint already returned is not repeated from the shop list. */
    setReviews(productReviews, shopReviews) {
      const { minRating, minLength } = this.settings;
      const seen = new Set();
      const keep = (review) => {
        if (!review.id || seen.has(review.id)) return false;
        seen.add(review.id);
        if (review.rating < minRating) return false;
        return reviewText(review).replace(/[^\p{L}\p{N}]/gu, '').length >= minLength;
      };

      this.reviews = [
        ...this.sortGroup(productReviews.filter(keep)),
        ...this.sortGroup(shopReviews.filter(keep)),
      ];

      if (!this.reviews.length) {
        this.fail('No Judge.me reviews to show yet for this source and these settings.');
        return;
      }

      this.prepareTabs();
      const active = this.tabs.find((tab) => !tab.hidden && tab.getAttribute('aria-pressed') === 'true');
      this.activate(active || this.tabs.find((tab) => !tab.hidden) || null);
    }

    sortGroup(list) {
      const time = (review) => Date.parse(review.date) || 0;
      switch (this.settings.sort) {
        case 'newest':
          return list.sort((a, b) => time(b) - time(a));
        case 'highest':
          return list.sort((a, b) => b.rating - a.rating || time(b) - time(a));
        case 'photos':
          return list.sort((a, b) => (b.photos.length > 0) - (a.photos.length > 0));
        default:
          return list;
      }
    }

    prepareTabs() {
      const { hideEmptyTabs, designMode } = this.settings;
      this.tabs.forEach((tab) => {
        const count = this.reviews.filter(tab.predicate).length;
        tab.disabled = false;
        tab.hidden = hideEmptyTabs && !designMode && count === 0 && tab.dataset.filter !== 'all';

        /* The editor shows every pill with its count, so a merchant can see
           which filter would be dropped on the live page and why. */
        if (designMode) {
          let badge = tab.querySelector('.pdp-reviews__tab-count');
          if (!badge) badge = tab.appendChild(create('span', 'pdp-reviews__tab-count'));
          badge.textContent = String(count);
        }
      });

      if (this.tabGroup) {
        this.tabGroup.hidden = this.tabs.filter((tab) => !tab.hidden).length < 2;
      }
    }

    activate(tab) {
      this.tabs.forEach((item) => item.setAttribute('aria-pressed', String(item === tab)));
      this.filtered = tab ? this.reviews.filter(tab.predicate) : this.reviews;
      this.visibleCount = this.settings.perPage;
      this.grid.scrollLeft = 0;
      this.render();
    }

    render() {
      const shown = this.filtered.slice(0, this.visibleCount);
      this.grid.replaceChildren(...shown.map((review) => this.buildCard(review)));
      this.afterRender(shown.length);
    }

    afterRender(shownCount) {
      const total = this.filtered.length;
      this.emptyMessage.hidden = total > 0;
      /* Once every review is showing the button stays in place, disabled,
         rather than vanishing — the space under the cards does not collapse
         and the shopper can see there is nothing further to load. It is only
         removed when the filter has no reviews at all. */
      this.moreButton.hidden = total === 0;
      this.moreButton.disabled = shownCount >= total;
      this.status.textContent = total ? `Showing ${shownCount} of ${total} reviews` : this.emptyMessage.textContent;
      requestAnimationFrame(() => this.revealReadMore());
    }

    onTabClick(event) {
      const tab = event.target.closest('.pdp-reviews__tab');
      if (!tab || tab.disabled || tab.getAttribute('aria-pressed') === 'true') return;
      this.activate(tab);
    }

    onBlockSelect(event) {
      const tab = this.tabs.find((item) => item === event.target || item.contains(event.target));
      if (tab && !tab.disabled) this.activate(tab);
    }

    /* Appends rather than re-rendering, and moves focus to the first new card
       so a keyboard or screen reader user carries on from where the new
       reviews begin instead of back at the top of the list.

       In the mobile swipe carousel the focus must not scroll: the new cards
       join the end of the row, and jumping the row to them would leave the
       shopper past the reviews they were reading. They stay where they are
       and swipe on to reach the new ones. */
    onMoreClick() {
      const start = this.visibleCount;
      const inCarousel = getComputedStyle(this.grid).overflowX !== 'visible';
      const scrollLeft = this.grid.scrollLeft;
      this.visibleCount += this.settings.perPage;
      const added = this.filtered.slice(start, this.visibleCount).map((review) => this.buildCard(review));
      this.grid.append(...added);
      this.afterRender(Math.min(this.visibleCount, this.filtered.length));
      if (added[0]) added[0].focus({ preventScroll: inCarousel });
      if (inCarousel) this.grid.scrollLeft = scrollLeft;
    }

    onGridClick(event) {
      const toggle = event.target.closest('.pdp-reviews__read-more');
      if (toggle) {
        const body = document.getElementById(toggle.getAttribute('aria-controls'));
        const expanded = toggle.getAttribute('aria-expanded') === 'true';
        body.classList.toggle('is-expanded', !expanded);
        toggle.setAttribute('aria-expanded', String(!expanded));
        toggle.textContent = expanded ? this.labels.readMore : this.labels.readLess;
        return;
      }

      const photo = event.target.closest('.pdp-reviews__photo');
      if (photo) this.openPhoto(photo.dataset.full, photo.dataset.alt);
    }

    openPhoto(url, alt) {
      if (!isSafeUrl(url)) return;
      if (!this.lightbox || typeof this.lightbox.showModal !== 'function') {
        window.open(url, '_blank', 'noopener');
        return;
      }
      this.lightboxImage.src = url;
      this.lightboxImage.alt = alt || this.photoLabel;
      this.lightbox.showModal();
    }

    /* A click on the backdrop lands on the dialog itself; the close button
       and the image are its children. */
    onLightboxClick(event) {
      if (event.target === this.lightbox || event.target.closest('[data-lightbox-close]')) {
        this.lightbox.close();
        this.lightboxImage.removeAttribute('src');
      }
    }

    /* "Read more" only appears on a card whose text really is cut off at the
       configured line count; a short review never gets a button that does
       nothing. */
    revealReadMore() {
      this.grid.querySelectorAll('.pdp-reviews__card-body.is-clamped:not(.is-expanded)').forEach((body) => {
        const toggle = body.nextElementSibling;
        if (!toggle || !toggle.classList.contains('pdp-reviews__read-more')) return;
        toggle.hidden = body.scrollHeight <= body.clientHeight + 1;
      });
    }

    buildCard(review) {
      const card = create('article', 'pdp-reviews__card');
      card.tabIndex = -1;

      const top = create('div', 'pdp-reviews__card-top');
      top.append(
        fillStars(create('span', 'pdp-reviews__stars'), review.rating, `Rated ${review.rating} out of 5`)
      );
      if (review.verified) top.append(create('span', 'pdp-reviews__badge', this.labels.verified));
      card.append(top);

      if (review.title) card.append(create('h3', 'pdp-reviews__card-title', review.title));

      if (review.paragraphs.length) {
        const clamp = this.settings.clamp;
        const body = create('div', `pdp-reviews__card-body${clamp > 0 ? ' is-clamped' : ''}`);
        body.id = `PdpReview-${review.id}-${Math.random().toString(36).slice(2, 7)}`;
        review.paragraphs.forEach((paragraph) => body.append(create('p', '', paragraph)));
        card.append(body);

        if (clamp > 0) {
          const toggle = create('button', 'pdp-reviews__read-more link', this.labels.readMore);
          toggle.type = 'button';
          toggle.hidden = true;
          toggle.setAttribute('aria-expanded', 'false');
          toggle.setAttribute('aria-controls', body.id);
          card.append(toggle);
        }
      }

      if (review.photos.length) {
        const list = create('ul', 'pdp-reviews__photos');
        list.setAttribute('role', 'list');
        review.photos.slice(0, MAX_PHOTOS).forEach((photo, index) => {
          const alt = `${this.photoLabel} ${index + 1}${review.author ? `, ${review.author}` : ''}`;
          const item = create('li');
          const button = create('button', 'pdp-reviews__photo');
          button.type = 'button';
          button.dataset.full = photo.full;
          button.dataset.alt = alt;
          button.setAttribute('aria-label', alt);
          const img = create('img');
          img.src = photo.thumb;
          img.alt = '';
          img.width = 72;
          img.height = 72;
          img.loading = 'lazy';
          img.decoding = 'async';
          button.append(img);
          item.append(button);
          list.append(item);
        });
        card.append(list);
      }

      const metaParts = [review.author, review.location];
      if (this.settings.showDate && review.date) {
        const date = new Date(review.date);
        if (!Number.isNaN(date.getTime())) {
          metaParts.push(
            new Intl.DateTimeFormat(document.documentElement.lang || undefined, {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            }).format(date)
          );
        }
      }
      const meta = metaParts.filter(Boolean).join(', ');
      if (meta) card.append(create('p', 'pdp-reviews__meta', meta));

      /* Only a review of a different product is labelled, so a shopper can
         tell a shop-wide review from one about the bag in front of them. */
      if (
        this.settings.showProduct &&
        review.productTitle &&
        handleOf(review.productUrl) !== this.settings.handle
      ) {
        const product = create('p', 'pdp-reviews__product');
        product.append(`${this.labels.product} `);
        if (review.productUrl && isSafeUrl(review.productUrl)) {
          const link = create('a', '', review.productTitle);
          link.href = review.productUrl;
          product.append(link);
        } else {
          product.append(review.productTitle);
        }
        card.append(product);
      }

      return card;
    }

    /* Nothing to show. On the live page the whole section steps aside rather
       than leaving a heading over an empty grid; the editor keeps it and says
       why, so the merchant can fix the setting. */
    fail(message) {
      this.grid.replaceChildren();
      this.moreButton.hidden = true;
      if (this.tabGroup) this.tabGroup.hidden = true;
      if (this.summary) this.summary.hidden = true;

      if (this.settings.designMode) {
        this.emptyMessage.textContent = message;
        this.emptyMessage.hidden = false;
        return;
      }
      const section = this.closest('.shopify-section');
      if (section) section.style.display = 'none';
    }
  }

  customElements.define('pdp-reviews', PdpReviews);
})();
